import { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { fontFamily } from '../../theme/tokens';
import type { SceneExample } from '../../data/scenes';

interface ExamplesSliderProps {
  /** Examples to render. Component re-mounts via React key when this list
   *  changes scene, so internal Embla state resets to first slide. */
  items: SceneExample[];
}

/**
 * Horizontal thumb slider with floating left/right arrows.
 *
 * Used under SceneCarousel to preview "what kind of result you can expect
 * from this scene". In MVP these are placeholder gradients; once we have
 * real poster frames in R2 they become background-image URLs (component
 * doesn't need to change — same shape).
 *
 * Arrows fade out (`disabled`) when the edge is reached so the user
 * doesn't get confused by a non-responsive tap target.
 */
export function ExamplesSlider({ items }: ExamplesSliderProps) {
  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    containScroll: 'trimSnaps',
    dragFree: true,
  });
  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(true);

  const update = useCallback(() => {
    if (!emblaApi) return;
    setCanPrev(emblaApi.canScrollPrev());
    setCanNext(emblaApi.canScrollNext());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    update();
    emblaApi.on('select', update);
    emblaApi.on('reInit', update);
    return () => {
      emblaApi.off('select', update);
      emblaApi.off('reInit', update);
    };
  }, [emblaApi, update]);

  const prev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const next = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    // Negative horizontal margin lets the slider bleed edge-to-edge while
    // the rest of the page maintains its 20px inset. We then re-apply the
    // padding INSIDE the scroll container so the first/last thumb still
    // has breathing room from the screen edge.
    <div style={{ position: 'relative', margin: '0 -20px', fontFamily }}>
      <ArrowButton direction="left" disabled={!canPrev} onClick={prev} />
      <ArrowButton direction="right" disabled={!canNext} onClick={next} />

      <div ref={emblaRef} style={{ overflow: 'hidden' }}>
        <div style={{ display: 'flex', gap: 8, padding: '0 20px' }}>
          {items.map((ex) => (
            <Thumb key={ex.id} item={ex} />
          ))}
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────
function ArrowButton({
  direction,
  disabled,
  onClick,
}: {
  direction: 'left' | 'right';
  disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      aria-label={direction === 'left' ? 'Previous example' : 'Next example'}
      style={{
        appearance: 'none',
        position: 'absolute',
        top: '50%',
        transform: 'translateY(-50%)',
        [direction]: 8,
        width: 36,
        height: 36,
        borderRadius: 999,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(8px)',
        color: '#fff',
        border: 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        cursor: disabled ? 'default' : 'pointer',
        boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
        opacity: disabled ? 0.3 : 1,
        pointerEvents: disabled ? 'none' : 'auto',
        transition: 'opacity 200ms',
      }}
    >
      <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round">
        {direction === 'left' ? (
          <polyline points="15 6 9 12 15 18" />
        ) : (
          <polyline points="9 6 15 12 9 18" />
        )}
      </svg>
    </button>
  );
}

function Thumb({ item }: { item: SceneExample }) {
  return (
    <div
      style={{
        flex: '0 0 100px',
        aspectRatio: '3 / 4',
        borderRadius: 12,
        background: item.gradient,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Bottom-fade overlay — gives the label legibility regardless of
          the underlying gradient's saturation at the bottom edge. */}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          background: 'linear-gradient(180deg, transparent 60%, rgba(0,0,0,0.4) 100%)',
        }}
      />
      <div
        style={{
          position: 'absolute',
          bottom: 6,
          left: 8,
          right: 8,
          fontSize: 10,
          fontWeight: 600,
          color: '#fff',
          textShadow: '0 1px 2px rgba(0,0,0,0.6)',
          zIndex: 1,
        }}
      >
        {item.label}
      </div>
    </div>
  );
}
